import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ListOffersQuerySchema, type ListOffersQuery } from '@ooh/contracts';
import { useOffersList } from '@ooh/screens';
import { useTranslation } from 'react-i18next';
import { useClientsList } from '../../features/clients/api';
import { useOfferAssignableUsers } from '../../features/offers/api';
import { OfferViewForbiddenState } from '../../features/offers/offer-view-forbidden-state';
import {
  OfferListErrorState,
  OfferListPage,
  OfferListPendingState,
} from '../../features/offers/list/offer-list-page';
import { useContextQuery } from '../../hooks/use-context-query';
import { useHasPermission } from '../../hooks/use-has-permission';
import { PageTitleContext } from '../__root';

// Route search params are view + assignedToUserIds only (CU-86cb4wjwk).
// ListOffersQuerySchema also carries cursor/pageSize for the useOffersList
// paginated fetch, which are hook-internal request params, not URL/bookmark
// state — validating the full schema here would reflect them into the
// address bar (e.g. /offers?pageSize=20&view=mine) on every navigation.
const OffersRouteSearchSchema = ListOffersQuerySchema.pick({
  view: true,
  assignedToUserIds: true,
}).strict();

export const Route = createFileRoute('/offers/')({
  validateSearch: OffersRouteSearchSchema,
  component: OffersRoute,
});

interface OffersRouteContentProps {
  canCreate: boolean;
  canManageAny: boolean;
  view: ListOffersQuery['view'];
  assignedToUserIds: number[];
  onViewChange: (view: ListOffersQuery['view']) => void;
  onAssignedToChange: (userIds: number[]) => void;
}

function OffersRouteContent({
  canCreate,
  canManageAny,
  onAssignedToChange,
  onViewChange,
  view,
  assignedToUserIds,
}: OffersRouteContentProps) {
  const offersQuery = useOffersList(
    view,
    assignedToUserIds.length > 0 ? assignedToUserIds : undefined,
  );
  const clientsQuery = useClientsList();
  const assignableUsersQuery = useOfferAssignableUsers();

  if (offersQuery.isLoading) {
    return <OfferListPendingState />;
  }

  if (offersQuery.isError) {
    return <OfferListErrorState onRetry={() => void offersQuery.refetch()} />;
  }

  return (
    <OfferListPage
      assignableUsers={assignableUsersQuery.data ?? []}
      assignedToUserIds={assignedToUserIds}
      canCreate={canCreate}
      canManageAny={canManageAny}
      clients={clientsQuery.data ?? []}
      offers={offersQuery.data ?? []}
      view={view}
      onAssignedToChange={onAssignedToChange}
      onViewChange={onViewChange}
    />
  );
}

export function OffersRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/offers/' });
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t } = useTranslation('offers');
  // Distinguishes "context still loading" from "confirmed no permission" for
  // the view=all -> view=mine redirect effect below — that effect performs a
  // one-shot URL rewrite (replace: true), so it must not fire off the
  // fail-closed canManageAny value during loading, or a user who actually
  // holds offer.view_all gets bounced to "mine" and stuck there (CU-86cb4wjwm).
  const isPermissionContextLoading = useContextQuery().data == null;
  const canViewAll = useHasPermission('offer.view_all', { loading: 'closed' });
  const canPrepare = useHasPermission('offer.prepare', { loading: 'closed' });
  const canView = canViewAll || canPrepare;
  const canManageAny = canViewAll;
  const canCreate = canPrepare;
  const view = search.view === 'all' && !canManageAny ? 'mine' : search.view;
  // assignedToUserIds only ever has an effect on the server within the 'all' view
  // (see OffersService.list), so it is irrelevant, and hidden, otherwise.
  const assignedToUserIds = canManageAny && view === 'all' ? (search.assignedToUserIds ?? []) : [];

  React.useEffect(() => {
    setPageTitle(t('list.pageTitle'));
  }, [setPageTitle, t]);

  React.useEffect(() => {
    if (search.view !== 'all' || canManageAny || isPermissionContextLoading) return;
    void navigate({ search: { view: 'mine' }, replace: true });
  }, [canManageAny, isPermissionContextLoading, navigate, search.view]);

  if (!canView) {
    return <OfferViewForbiddenState />;
  }

  function handleViewChange(nextView: ListOffersQuery['view']) {
    if (nextView === 'all' && !canManageAny) return;
    void navigate({ search: { view: nextView } });
  }

  function handleAssignedToChange(userIds: number[]) {
    void navigate({
      search: { view, assignedToUserIds: userIds.length > 0 ? userIds : undefined },
    });
  }

  return (
    <OffersRouteContent
      assignedToUserIds={assignedToUserIds}
      canCreate={canCreate}
      canManageAny={canManageAny}
      view={view}
      onAssignedToChange={handleAssignedToChange}
      onViewChange={handleViewChange}
    />
  );
}
